/**
 * Textos y destinos de las alertas de asignación (`.claude/alertas_crm/`).
 * Los comparten la campana (`AssignmentAlertsSection`) y los avisos
 * emergentes (`AssignmentToasts`), así los dos llevan al mismo sitio.
 */
import { haceRelativo } from "@/modules/fxCrm/helpers/relativeTime";

export const plural = (n, una, varias) => `${n} ${n === 1 ? una : varias}`;

/**
 * A dónde lleva una alerta, con sus interacciones a resaltar (`?highlight=`):
 * coordinador o transferencia → su Bandeja; gestor → Gestión Interacciones.
 *
 * @param {Array} ids  ids de gestión.
 * @param {{ tipo?: string, rol: string }} contexto
 */
export function rutaDeAlerta(ids, { tipo, rol }) {
  const name =
    tipo === "transfer" || rol === "coordinator" ? "fx-inbox" : "fx-interaction-no-management";
  return { name, query: { highlight: ids.join(",") } };
}

/** Las que siguen pendientes; si ya no queda ninguna, todas (para ubicarlas). */
export function idsParaResaltar(alerta) {
  const pendientes = alerta.items.filter((item) => item.estado !== "resuelta");
  return (pendientes.length ? pendientes : alerta.items).map((item) => item.id);
}

const nuevas = (n) => plural(n, "interacción nueva", "interacciones nuevas");
const verbo = (tipo, n) =>
  tipo === "transfer" ? (n === 1 ? "Transferida" : "Transferidas") : n === 1 ? "Asignada" : "Asignadas";
const textoVer = (n) => (n === 1 ? "Ver la interacción" : "Ver las interacciones");

/**
 * Aviso al destinatario cuando una alerta LLEGA en vivo (socket o simulador):
 * «Sebastián, tienes 3 interacciones nuevas · Asignadas por Andrea · ahora.»
 */
export function avisoLlegada(alerta, { nombre, rol }) {
  const n = alerta.items.length;
  return {
    tipo: "recibida",
    titulo: `${nombre}, tienes ${nuevas(n)}`,
    texto: `${verbo(alerta.tipo, n)} por ${alerta.de?.nombre || "—"} · ${haceRelativo(alerta.fecha)}.`,
    accion: {
      label: textoVer(n),
      ruta: rutaDeAlerta(idsParaResaltar(alerta), { tipo: alerta.tipo, rol }),
    },
  };
}

/**
 * Resumen al ENTRAR al CRM con asignaciones sin ver (task_4 CA-02). Varias
 * alertas se juntan en un aviso: «… Asignadas por Andrea y 1 más · hace 2 h.»
 * `null` si no hay nada sin ver.
 */
export function avisoEntrada(recibidas, { nombre, rol }) {
  const conNuevas = recibidas.filter((alerta) => alerta.items.some((item) => item.estado === "nueva"));
  if (!conNuevas.length) return null;

  const ids = conNuevas.flatMap((alerta) =>
    alerta.items.filter((item) => item.estado === "nueva").map((item) => item.id)
  );
  const remitentes = [...new Set(conNuevas.map((alerta) => alerta.de?.nombre).filter(Boolean))];
  const resto = remitentes.length - 1;
  const quien = remitentes.length ? `${remitentes[0]}${resto > 0 ? ` y ${resto} más` : ""}` : "—";
  const tipos = new Set(conNuevas.map((alerta) => alerta.tipo));
  const tipo = tipos.size === 1 ? [...tipos][0] : "assign";
  // La más reciente: `recibidas` va de la más nueva a la más vieja.
  const fecha = conNuevas[0].fecha;

  return {
    tipo: "recibida",
    titulo: `${nombre}, tienes ${nuevas(ids.length)}`,
    texto: `${verbo(tipo, ids.length)} por ${quien} · ${haceRelativo(fecha)}.`,
    accion: { label: textoVer(ids.length), ruta: rutaDeAlerta(ids, { tipo, rol }) },
  };
}

/** Nombres que se listan en el aviso de un reparto antes de «y N más». */
const MAX_NOMBRES_REPARTO = 4;

/**
 * Aviso al REMITENTE al confirmar (task_4 CA-01): «Alerta enviada a Luis ·
 * 3 interacciones asignadas. La verá en su campana y en sus tarjetas.»
 *
 * @param {{ tipo: "transfer"|"assign", para?: string, n: number, reparto?: string,
 *           porPersona?: Array<{ nombre: string, n: number }> }} envio
 *   `reparto` (título del modo) cuando lo repartió el servidor; `porPersona`
 *   es cómo repartió (`by_user` de `assign/`). Sin él, el aviso va genérico.
 */
export function avisoEnvio({ tipo, para, n, reparto, porPersona = [] }) {
  // Reparto automático con el detalle del servidor: se nombra a cada uno.
  if (reparto && porPersona.length === 1) {
    return avisoEnvio({ tipo, para: porPersona[0].nombre, n });
  }
  if (reparto && porPersona.length > 1) {
    const nombrados = porPersona.slice(0, MAX_NOMBRES_REPARTO).map((p) => `${p.n} para ${p.nombre}`);
    const resto = porPersona.length - MAX_NOMBRES_REPARTO;
    return {
      tipo: "enviada",
      titulo: `Alertas enviadas a ${porPersona.length} personas`,
      texto: `${nombrados.join(", ")}${resto > 0 ? ` y ${resto} más` : ""} · ${reparto.toLowerCase()}. Cada uno las verá en su campana y en sus tarjetas.`,
    };
  }
  if (reparto) {
    return {
      tipo: "enviada",
      titulo: "Alertas enviadas a tu equipo",
      texto: `${plural(n, "interacción repartida", "interacciones repartidas")} · ${reparto.toLowerCase()}. Cada uno la verá en su campana y en sus tarjetas.`,
    };
  }
  const hecho =
    tipo === "transfer"
      ? plural(n, "interacción transferida", "interacciones transferidas")
      : plural(n, "interacción asignada", "interacciones asignadas");
  const donde = tipo === "transfer" ? "en su Bandeja" : "en sus tarjetas";
  return {
    tipo: "enviada",
    titulo: `Alerta enviada a ${para || "el destinatario"}`,
    texto: `${hecho}. La verá en su campana y ${donde}.`,
  };
}
